/**
 * Capability manifest API (R20 chat 3)
 *
 * GET /api/capabilities?channel_id=  — one channel's manifest, in two halves:
 *
 *   capabilities  What an overlay can bind to. AVAILABLE ONLY: a disabled
 *                 module's capabilities are absent. Fetched and typed here;
 *                 its first renderer will be the overlay editor's binding
 *                 picker, which is not built yet.
 *   variables     What a command's response can write in `$(...)`. EVERY
 *                 variable, available or not, each with its status in this
 *                 channel and what it prints instead of a value. Rendered by
 *                 components/VariableHelp.tsx on the Commands page.
 *
 * GET /api/admin/variables  — the same variables with no channel (status
 * null). Super-admin only; Admin → Commands' help. Both listings are built by
 * one backend function, so they never describe a variable differently.
 *
 * This route had no caller from M1 until R20 chat 3, and was untested for it.
 */

import api from '@/api/client'

export interface CapabilityInfo {
  name: string
  kind: 'property' | 'event'
  /** "core:<x>" or "module:<name>". */
  provider: string
  description: string
  payload_schema: Record<string, unknown> | null
}

/** Where a variable's value comes from. */
export type VariableSource = 'context' | 'core' | 'module'

/**
 * A variable's state in one channel: "available", or the REASON it is printing
 * instead — which is also the `reason` of one of its `degrades`, so the text
 * it prints right now is `degrades.find(d => d.reason === status)`.
 */
export type VariableStatus = 'available' | 'disabled' | 'unresolved' | 'unknown'

export interface VariableDegrade {
  /** never_pushed / stale / offline / no_record / disabled / unresolved / unknown, or a module's own. */
  reason: string
  /** Exactly what the command prints in chat for this reason. */
  text: string
  /** When a core reason applies, in an author's words. Null for a module's own reasons. */
  when: string | null
}

export interface VariableInfo {
  /** What the author writes inside `$(...)`. */
  name: string
  description: string
  source: VariableSource
  /** The owning module, for source "module". */
  module: string | null
  /** The capability the value is read from. Null for context variables. */
  capability: string | null
  /** Null in the admin listing (no channel). */
  status: VariableStatus | null
  /** Everything this variable may print instead of a value. Empty for context variables. */
  degrades: VariableDegrade[]
}

export interface CapabilityManifest {
  capabilities: CapabilityInfo[]
  variables: VariableInfo[]
}

export const capabilitiesApi = {
  /** One channel's manifest. 404 for a channel the caller cannot see. */
  manifest(channelId: number): Promise<CapabilityManifest> {
    return api.get<CapabilityManifest>(
      `/api/capabilities?channel_id=${encodeURIComponent(String(channelId))}`,
    )
  },

  /** Every variable, no channel. Super-admin only (403 otherwise). */
  adminVariables(): Promise<VariableInfo[]> {
    return api.get<VariableInfo[]>('/api/admin/variables')
  },
}

export default capabilitiesApi
